import { PortableTextComponents } from "@portabletext/react";
import Link from "next/link";
import { getLocale, getTranslations } from "next-intl/server";
import { local } from "@/local";
import RichText from "./rich-text";
import { QuoteIcon } from "lucide-react";
import { SanityImage } from "./sanity-image";

export const components = {
  marks: {
    link: ({ children, value }) => {
      const rel = !value.href.startsWith("/")
        ? "noreferrer noopener"
        : undefined;
      return (
        <Link
          href={value.href}
          rel={rel}
          target={!value.href.startsWith("/") ? "_blank" : "_self"}
          className="!text-current !underline hover:!text-accent"
        >
          {children}
        </Link>
      );
    },
  },
  types: {
    customImage: async ({ value }) => {
      return (
        <div className="flex justify-center mb-4 lg:mb-8 last:mb-0 w-full">
          <SanityImage className="rounded-lg" image={value} />
        </div>
      );
    },
    audio: async ({ value }) => {
      const t = await getTranslations("axe");

      return (
        <div className="mb-4 lg:mb-8 last:mb-0">
          <audio controls className="w-full">
            <source src={value.url} type="audio/mpeg" />
            {t("audioSupport")}
            <a href={value.url} download className="text-accent underline">
              {t("downloadAudio")}
            </a>
          </audio>
        </div>
      );
    },
    video: async ({ value }) => {
      const t = await getTranslations("axe");
      return (
        <div className="mb-4 lg:mb-8 last:mb-0 flex justify-center">
          <video
            className="rounded-lg"
            controls
            width="640"
            height="360"
            preload="metadata"
          >
            {" "}
            <source src={value.url} type="video/mp4" />
            <source src={value.url} type="video/mp4" />
            {t("videoSupport")}
            <a href={value.url} download className="text-accent underline">
              {t("downloadVideo")}
            </a>
          </video>
        </div>
      );
    },
    box: async ({ value }) => {
      const locale = await getLocale();
      return (
        <div className="bg-accent-light shadow-lg rounded-lg p-4 lg:p-8 mb-4 lg:mb-8 last:mb-0">
          <RichText text={local(locale, value.text)} />
        </div>
      );
    },
    imageBesideText: async ({ value }) => {
      const locale = await getLocale();
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-4 lg:mb-8 last:mb-0">
          <div
            className={
              value.imagePosition === "left" ? "order-0 md:order-1" : "order-0"
            }
          >
            <RichText text={local(locale, value.text)} />
          </div>
          <div
            className={
              value.imagePosition === "left" ? "order-1 md:order-0" : "order-1"
            }
          >
            <SanityImage
              className="rounded-lg object-cover"
              image={value.image}
            />
          </div>
        </div>
      );
    },
    embed: async ({ value }) => {
      return (
        <div
          className="flex justify-center"
          dangerouslySetInnerHTML={{ __html: value.code }}
        />
      );
    },
  },
  block: {
    h1: ({ children }) => (
      <h1 className="mb-6 lg:mb-14 last:mb-0 ">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="mb-6 lg:mb-6 last:mb-0">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-6 lg:mb-6 last:mb-0 ">{children}</h3>
    ),
    h4: ({ children }) => (
      <h4 className="mb-6 lg:mb-6 last:mb-0">{children}</h4>
    ),
    h5: ({ children }) => <p className="mb-6 lg:mb-6 last:mb-0">{children}</p>,
    normal: ({ children }) => (
      <p className="mb-4 lg:mb-8 last:mb-0">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="mb-8 lg:mb-14 last:mb-0 bg-primary shadow-lg rounded-lg text-neutral py-4 px-8 flex gap-4 items-center">
        <div className="w-5 h-5">
          <QuoteIcon className="w-5 h-5" />
        </div>
        <h3>{children}</h3>
      </blockquote>
    ),
  },
} as const satisfies PortableTextComponents;
